import { Group, Stack, createStyles, useMantineTheme } from '@mantine/core';
import type { IBaseModel } from '@repo/common/models/baseModel';
import { Text } from '@repo/foundations';
import { type MouseEvent } from 'react';

const useStyles = createStyles((theme) => ({
	itemGroup: {
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: theme.other.getColor('surface/secondary/default'),
		},
		borderRadius: theme.radius.sm,
	},
	iconContainer: {
		width: 28,
		height: 28,
		flexShrink: 0,
		backgroundColor: theme.other.getColor('surface/secondary/default'),
		borderRadius: theme.radius.sm,
	},
	contentStack: {
		flex: 1,
		maxWidth: '100%',
	},
	titleText: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	subtitleText: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		maxWidth: '90%',
	},
	item: {
		borderRadius: theme.radius.sm,
	},
}));

export interface SuggestionItemTableProps<T extends IBaseModel> {
	items: T[];
	iconRenderer: (item: T) => React.ReactNode;
	titleSelector: (item: T) => string;
	subTextSelector: (item: T) => string;
	onItemClick?: (item: T, index: number, event: MouseEvent) => void;
}

export function SuggestionItemTable<T extends IBaseModel>({
	items,
	iconRenderer,
	titleSelector,
	subTextSelector,
	onItemClick,
}: SuggestionItemTableProps<T>) {
	const { classes } = useStyles();
	const theme = useMantineTheme();

	const handleItemClick = (item: T, index: number, event: MouseEvent) => {
		onItemClick?.(item, index, event);
	};

	return (
		<Stack
			spacing={0}
			p={theme.other.space[1]}
			className={classes.item}
			w="auto"
		>
			{items.map((item, index) => (
				<Group
					key={item.id}
					align="center"
					onClick={(event) => handleItemClick(item, index, event)}
					px="sm"
					py="xs"
					noWrap
					className={onItemClick ? classes.itemGroup : undefined}
				>
					<Group
						className={classes.iconContainer}
						align="center"
						position="center"
					>
						{iconRenderer(item)}
					</Group>
					<Stack spacing={0} className={classes.contentStack}>
						<Text size="sm" weight="semibold" className={classes.titleText}>
							{titleSelector(item)}
						</Text>
						<Text
							size="xs"
							color="text/secondary/default"
							className={classes.subtitleText}
						>
							{subTextSelector(item)}
						</Text>
					</Stack>
				</Group>
			))}
		</Stack>
	);
}
